/* import __COLOCATED_TEMPLATE__ from './run-procedure.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type IntlService from 'ember-intl/services/intl';
import { request, postRequest } from 'embercom/lib/inbox/requests';
import { captureException } from 'embercom/lib/sentry';

interface TriggerableProcedure {
  id: number;
  name: string;
}

interface Signature {
  Args: {
    conversation: { id: number };
  };
}

export default class RunProcedure extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: any;

  @tracked procedures: TriggerableProcedure[] | null = null;
  @tracked isLoading = false;
  @tracked selectedProcedureId: number | null = null;
  @tracked isTriggering = false;

  @action
  async loadProcedures() {
    if (this.isLoading || this.procedures !== null) {
      return;
    }
    this.isLoading = true;
    try {
      let response = await request(
        `/ember/inbox/conversations/triggerable_procedures?app_id=${this.session.workspace.id}`,
      );
      let data = (await response.json()) as { procedures: TriggerableProcedure[] };
      this.procedures = data.procedures;
    } catch (error) {
      captureException(error);
      this.procedures = [];
    } finally {
      this.isLoading = false;
    }
  }

  @action
  selectProcedure(event: Event) {
    let value = (event.target as HTMLSelectElement).value;
    this.selectedProcedureId = value ? Number(value) : null;
  }

  @action
  async triggerSelected() {
    if (!this.selectedProcedureId) {
      return;
    }
    this.isTriggering = true;
    try {
      await postRequest(
        `/ember/inbox/conversations/${this.args.conversation.id}/trigger_procedure`,
        { procedure_id: this.selectedProcedureId, app_id: this.session.workspace.id },
      );
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.conversation-details-sidebar.run-procedure.error'),
      );
    } finally {
      this.isTriggering = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::RunProcedure': typeof RunProcedure;
    'inbox2/conversation-details-sidebar/sidebar-sections/run-procedure': typeof RunProcedure;
  }
}
